import { useCallback, useEffect, useState } from "react";
import { ReactFlowProvider } from "@xyflow/react";
import { GraphEditor } from "~/components/GraphEditor";
import { Help } from "~/components/Help";
import { PresetsDrawer } from "~/components/PresetsDrawer";
import { Viewer3D } from "~/components/Viewer3D";
import { Button } from "~/components/ui/Button";
import { Dialog } from "~/components/ui/Dialog";
import { Snackbar } from "~/components/ui/Snackbar";
import { Spinner } from "~/components/ui/Spinner";
import { TextInput } from "~/components/ui/TextInput";
import { getGeneratedPoints } from "~/lib/scene";
import { presets } from "~/presets";
import { useGraphStore } from "~/store/graphStore";

/** Firestore is only touched on save/load, so it stays out of the initial bundle. */
const firestore = () => import("~/lib/firebase");

/** Sketch code from the URL, e.g. `/5iGW5G6Lyh48uoy8toEy`. */
function codeFromUrl(): string {
  return window.location.pathname.replace(/^\/+/, "").trim();
}

export function App() {
  const loadGraph = useGraphStore((state) => state.loadGraph);
  const toJSON = useGraphStore((state) => state.toJSON);

  const [busy, setBusy] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showPresets, setShowPresets] = useState(false);
  const [showLoadDialog, setShowLoadDialog] = useState(false);
  const [loadCode, setLoadCode] = useState("");
  const [savedCode, setSavedCode] = useState<string | null>(null);
  const [toast, setToast] = useState("");

  const load = useCallback(
    async (code: string) => {
      if (!code) return;
      setBusy(true);

      try {
        const { loadSketch } = await firestore();
        const data = await loadSketch(code);
        if (!data) {
          setToast("That code doesn't match a saved configuration");
          return;
        }

        loadGraph(data);
        window.history.replaceState(null, "", `/${code}`);
        setShowLoadDialog(false);
      } catch (error) {
        setToast(
          error instanceof Error ? error.message : "Could not load that sketch",
        );
      } finally {
        setBusy(false);
      }
    },
    [loadGraph],
  );

  // Open whatever the URL points at, falling back to the first demo preset.
  useEffect(() => {
    void load(codeFromUrl() || presets[0]!.code);
  }, [load]);

  const save = useCallback(async () => {
    setBusy(true);

    try {
      const { saveSketch } = await firestore();
      const code = await saveSketch(toJSON());
      window.history.replaceState(null, "", `/${code}`);
      setSavedCode(code);
    } catch (error) {
      setToast(
        error instanceof Error ? error.message : "Could not save this sketch",
      );
    } finally {
      setBusy(false);
    }
  }, [toJSON]);

  const exportPoints = useCallback(() => {
    const blob = new Blob([JSON.stringify(getGeneratedPoints(), null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `stack_${Date.now()}.json`;
    link.click();

    URL.revokeObjectURL(url);
  }, []);

  return (
    <div className="flex h-full flex-col bg-canvas text-neutral-100">
      <main className="flex min-h-0 flex-1 flex-col xl:flex-row">
        <Viewer3D />

        <div className="relative min-h-0 flex-1 border-neutral-800 xl:border-l">
          <ReactFlowProvider>
            <GraphEditor
              onSave={() => void save()}
              onLoad={() => setShowLoadDialog(true)}
              onExportPoints={exportPoints}
            />
          </ReactFlowProvider>
        </div>
      </main>

      <div className="fixed top-2.5 left-2.5 z-30 flex flex-col items-start gap-2">
        <Button variant="secondary" onClick={() => setShowHelp(true)}>
          Help
        </Button>
        <PresetsDrawer
          open={showPresets}
          presets={presets}
          onOpen={() => setShowPresets(true)}
          onClose={() => setShowPresets(false)}
          onSelect={(preset) => void load(preset.code)}
        />
      </div>

      {showHelp ? <Help onClose={() => setShowHelp(false)} /> : null}
      {busy ? <Spinner /> : null}

      <Dialog
        open={savedCode !== null}
        title="Your configuration has been saved"
        onClose={() => setSavedCode(null)}
        footer={<Button onClick={() => setSavedCode(null)}>OK</Button>}
      >
        <p>
          Save this code to load it in the future:{" "}
          <code className="font-mono text-teal-300">{savedCode}</code>
        </p>
        <p className="mt-2 text-neutral-400">(Or bookmark this URL.)</p>
      </Dialog>

      <Dialog
        open={showLoadDialog}
        title="Load node configuration"
        onClose={() => setShowLoadDialog(false)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowLoadDialog(false)}>
              Cancel
            </Button>
            <Button onClick={() => void load(loadCode)}>Load</Button>
          </>
        }
      >
        <label className="block">
          Enter your code to load a previously saved configuration:
          <TextInput
            autoFocus
            value={loadCode}
            placeholder="e.g. 5iGW5G6Lyh48uoy8toEy"
            onChange={(event) => setLoadCode(event.target.value)}
            className="mt-2"
          />
        </label>
      </Dialog>

      <Snackbar
        open={toast !== ""}
        message={toast}
        onClose={() => setToast("")}
      />
    </div>
  );
}
